/**
 * Spend Sankey. Pure-SVG renderer over a d3-sankey layout — left axis
 * (business function or team) flows to the right axis (investment area or
 * treatment), link thickness ∝ dollars.
 *
 * d3-sankey computes geometry only; the SVG (and therefore every colour,
 * label, and font) is ours, so the diagram inherits the same tokens as the
 * rest of the accounting surface and renders identically across browsers.
 * The chart is drawn in a fixed logical viewBox and scaled to its
 * container via `width: 100%`, which sidesteps any resize-observer plumbing.
 */

import {
  sankey,
  sankeyJustify,
  sankeyLinkHorizontal,
  type SankeyNode,
} from 'd3-sankey';
import { useMemo } from 'react';

import styles from './Accounting.module.css';
import { fmtUSD } from './rollups';
import {
  CATEGORY_COLORS,
  type SankeyGraphData,
  type SankeyLinkDatum,
  type SankeyNodeDatum,
} from './sankey-data';

type SNode = SankeyNode<SankeyNodeDatum, SankeyLinkDatum>;

// Logical canvas. Width scales to the container; the side margins reserve
// room for the node labels (long function names on the left, short
// category names on the right).
const W = 940;
// Keep the label gutters tight so the flow area gets most of the width.
// The right labels are short categories, so their gutter is narrower.
const LEFT_LABEL = 132;
const RIGHT_LABEL = 90;
const NODE_W = 13;
// Left-axis bars render thinner than the layout node width.
const LEFT_NODE_W = 5;
const NODE_PAD = 30;
const PAD_TOP = 20;
const PAD_BOTTOM = 20;

// Left-axis (business function / team) node bars — dark gray.
const LEFT_NODE_FILL = '#333333';

/** Truncate a node label to `max` chars, appending an ellipsis if longer. */
function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

export function SpendSankey({ data }: { data: SankeyGraphData }) {
  const leftCount = data.nodes.filter((n) => n.side === 'left').length;
  const rightCount = data.nodes.filter((n) => n.side === 'right').length;
  const maxNodes = Math.max(leftCount, rightCount, 1);
  // ~104px per node, floored at 440 and capped at 800 so a tall axis (many
  // teams / functions) doesn't run the diagram off the screen.
  const height = Math.min(800, Math.max(440, maxNodes * 104));

  const graph = useMemo(() => {
    if (data.nodes.length === 0 || data.links.length === 0) return null;
    const layout = sankey<SankeyNodeDatum, SankeyLinkDatum>()
      .nodeId((d) => d.id)
      .nodeWidth(NODE_W)
      .nodePadding(NODE_PAD)
      .nodeAlign(sankeyJustify)
      .extent([
        [LEFT_LABEL, PAD_TOP],
        [W - RIGHT_LABEL, height - PAD_BOTTOM],
      ]);
    return layout({
      nodes: data.nodes.map((d) => ({ ...d })),
      links: data.links.map((d) => ({ ...d })),
    });
  }, [data, height]);

  if (!graph) return null;

  const linkPath = sankeyLinkHorizontal<SankeyNodeDatum, SankeyLinkDatum>();

  return (
    <svg
      className={styles.sankey}
      viewBox={`0 0 ${W} ${height}`}
      preserveAspectRatio='xMidYMid meet'
      role='img'
      aria-label='Spend flow from the left axis to the right axis'
    >
      <g>
        {graph.links.map((link, i) => {
          const target = link.target as SNode;
          const source = link.source as SNode;
          const color = CATEGORY_COLORS[target.category] ?? '#9bbf74';
          const d = linkPath(link) ?? undefined;
          return (
            <path
              key={i}
              className={styles.sankeyLink}
              d={d}
              stroke={color}
              strokeWidth={Math.max(1, link.width ?? 1)}
            >
              <title>
                {source.label} → {target.label}: {fmtUSD(link.value)}
              </title>
            </path>
          );
        })}
      </g>
      <g>
        {graph.nodes.map((node) => {
          const n = node as SNode;
          const x0 = n.x0 ?? 0;
          const x1 = n.x1 ?? 0;
          const y0 = n.y0 ?? 0;
          const y1 = n.y1 ?? 0;
          const isLeft = n.side === 'left';
          const fill = isLeft ? LEFT_NODE_FILL : CATEGORY_COLORS[n.category] ?? '#9bbf74';
          // Left (function / team) bars are drawn thin, right-anchored to
          // the slot's right edge where their links attach; right
          // (investment / treatment) bars keep the full node width.
          const rectW = isLeft ? LEFT_NODE_W : Math.max(1, x1 - x0);
          const rectX = isLeft ? x1 - LEFT_NODE_W : x0;
          // Left labels hug their (thin) bar right-aligned; right labels
          // hug theirs left-aligned.
          const labelX = isLeft ? rectX - 9 : x1 + 9;
          const anchor = isLeft ? 'end' : 'start';
          const midY = (y0 + y1) / 2;
          return (
            <g key={n.id}>
              <rect
                className={styles.sankeyNode}
                x={rectX}
                y={y0}
                width={rectW}
                height={Math.max(1, y1 - y0)}
                fill={fill}
              >
                <title>
                  {n.label}: {fmtUSD(n.value ?? 0)}
                </title>
              </rect>
              <text
                className={styles.sankeyLabel}
                x={labelX}
                y={midY - 4}
                textAnchor={anchor}
              >
                {truncate(n.label, 24)}
              </text>
              <text
                className={styles.sankeyAmount}
                x={labelX}
                y={midY + 12}
                textAnchor={anchor}
              >
                {fmtUSD(n.value ?? 0, { compact: true })}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}
